import {
  Button,
  FieldLabel,
  FieldWrapper,
  FlexBox,
  IconButton,
  InputText,
  Tooltip,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { useEffect, useState } from 'react';

import { useBreakpointValue } from 'utils/mediaQuery';

import {
  getAuthorizationKeyMessage,
  getEmptyInputMessage,
  getKeyValidationMessage,
} from '../../utils/validateMessages';

import styles from './customHeaders.module.scss';

type CustomHeadersProps = {
  value?: Dictionary<string>;
  onChange: (value?: Partial<Dictionary<string>>) => void;
  disabled: boolean;
};

type CustomHeader = {
  key: string;
  value: string;
};

export const CustomHeaders = ({
  onChange,
  value,
  disabled,
}: CustomHeadersProps): JSX.Element => {
  const [customHeaders, setCustomHeaders] = useState<readonly CustomHeader[]>(
    [],
  );

  const isSmallScreen = useBreakpointValue({ base: true, md: false });

  const handleOnChange = (headers: readonly CustomHeader[]) => {
    onChange(
      headers.reduce(
        (obj, current) => ({
          ...obj,
          [current.key.toLowerCase()]: current.value,
        }),
        {},
      ),
    );
  };

  const handleCustomHeaderChange = (
    key: string,
    value: string,
    indexToChange: number,
  ) => {
    const changedHeaders = customHeaders.map((header, index) =>
      index === indexToChange
        ? {
            key,
            value,
          }
        : header,
    );

    setCustomHeaders(changedHeaders);
    handleOnChange(changedHeaders);
  };

  const handleCustomHeaderRemoval = (indexToRemove: number) => {
    const changedHeaders = customHeaders.filter(
      (_header, index) => index !== indexToRemove,
    );

    setCustomHeaders(changedHeaders);
    handleOnChange(changedHeaders);
  };

  const handleCustomHeaderAddition = () => {
    const changedHeaders = [
      ...customHeaders,
      {
        key: '',
        value: '',
      },
    ];

    setCustomHeaders(changedHeaders);
    handleOnChange(changedHeaders);
  };

  useEffect(() => {
    if (value && Object.keys(value).length > 0 && customHeaders.length === 0) {
      setCustomHeaders(
        Object.entries(value).map(([key, value]) => ({ key, value })),
      );
      onChange(value);
    }
  }, [value]);

  useEffect(() => {
    if (customHeaders.length === 0) {
      onChange(undefined);
    }
  }, [customHeaders]);

  return (
    <FlexBox flexDirection="column" gap="200" margin="0 0 300 0">
      <FlexBox>
        <FieldLabel
          helpIcon={{
            dark: true,
            title: 'Custom headers',
            text: 'You can specify your own custom headers as key/value pair. However, the key `authorization` is not allowed as custom header (use the input field above for that).',
          }}
        >
          Custom headers
        </FieldLabel>
      </FlexBox>

      <FlexBox flexDirection="column" gap="200">
        {customHeaders.length > 0 ? (
          customHeaders.map(({ key, value }, headerIndex) => (
            <FlexBox
              padding="300"
              className={styles.customHeaders}
              flexDirection={isSmallScreen ? 'column' : 'row'}
              gap={isSmallScreen ? '300' : '200'}
              key={headerIndex}
            >
              <FieldWrapper
                required
                disabled={disabled}
                key={`${headerIndex}-key`}
                label="Key"
                helpIcon={{
                  dark: true,
                  title: 'Rules when adding custom headers',
                  text: (
                    <ul>
                      <li>
                        No spaces are allowed in the <i>key</i> field
                      </li>
                      <li>
                        Don&apos;t use special unicode characters in the{' '}
                        <i>key</i> field
                      </li>
                      <li>
                        Uppercase <i>key</i> values will be transformed to
                        lowercase
                      </li>
                      <li>
                        The key <i>authorization</i> is not allowed here -
                        please use the <i>Authorization header</i> input
                        instead.
                      </li>
                    </ul>
                  ),
                }}
                meta={{
                  error:
                    getEmptyInputMessage(key) ||
                    getAuthorizationKeyMessage(key) ||
                    getKeyValidationMessage(key),
                  warning: /(.*[A-Z].*)/.test(key)
                    ? 'Uppercase characters will be saved with lowercase characters.'
                    : undefined,
                }}
              >
                <InputText
                  disabled={disabled}
                  status={getEmptyInputMessage(key) ? 'error' : undefined}
                  value={key}
                  onChange={(event) => {
                    const changedKey = event.target.value;
                    handleCustomHeaderChange(changedKey, value, headerIndex);
                  }}
                  placeholder="Key"
                />
              </FieldWrapper>
              <FieldWrapper
                required
                disabled={disabled}
                key={`${headerIndex}-value`}
                label="Value"
                meta={{
                  // We want to check the value field only when the 'key' is not empty
                  error: !getEmptyInputMessage(key)
                    ? getEmptyInputMessage(value)
                    : undefined,
                }}
              >
                <InputText
                  disabled={disabled}
                  status={
                    !getEmptyInputMessage(key) &&
                    Boolean(getEmptyInputMessage(value))
                      ? 'error'
                      : undefined
                  }
                  value={value}
                  onChange={(event) => {
                    const changedValue = event.target.value;
                    handleCustomHeaderChange(key, changedValue, headerIndex);
                  }}
                  placeholder="Value"
                />
              </FieldWrapper>
              <FlexBox
                alignItems="center"
                padding={isSmallScreen ? undefined : '0 0 0 300'}
                justifyContent={isSmallScreen ? 'end' : undefined}
              >
                <Tooltip dark content="Remove header">
                  <IconButton
                    disabled={disabled}
                    variant="secondary"
                    icon="trash"
                    onClick={() => handleCustomHeaderRemoval(headerIndex)}
                  />
                </Tooltip>
              </FlexBox>
            </FlexBox>
          ))
        ) : (
          <FlexBox
            padding="250 400"
            className={classnames(
              styles.noCustomHeadersMessage,
              styles.customHeaders,
            )}
          >
            No custom headers added
          </FlexBox>
        )}
      </FlexBox>

      <FlexBox alignItems="flex-start" margin="0 0 100 0">
        <Button
          disabled={disabled}
          icon="plus"
          variant="secondary"
          onClick={() => handleCustomHeaderAddition()}
        >
          Add custom header
        </Button>
      </FlexBox>
    </FlexBox>
  );
};
